import { createElement } from 'react';
import type { OptionTypeBase } from 'react-select';

import { type ResolutionConfigItemType } from '@frontend/api';
import {
  Select,
  type SelectProps,
  SelectComponents,
} from '@frontend/legacy-components';

import { ICON_STYLES } from '../utils/cascader';
import { getResolutionIcon, getIsDestructive } from '../utils/resolutions';

export type ResolutionTypeOption = Omit<OptionTypeBase, 'value'> & {
  value: ResolutionConfigItemType;
};

export const EmptyGroupHeading = () => null;

export const IconOption = (
  props: SelectComponents.OptionProps<ResolutionTypeOption, false>,
) => {
  const Icon = getResolutionIcon(props.data.value);
  const isDestructive = getIsDestructive(props.data.value);

  return (
    <SelectComponents.Option
      {...props}
      innerProps={{
        alignItems: 'center',
        ...props.innerProps,
      }}
      className="resolution-type-option"
    >
      {Icon &&
        createElement(Icon, {
          ...ICON_STYLES,
          color: isDestructive
            ? 'interaction.destructive'
            : 'feature.secondary',
          mr: 1,
        })}
      {props.children}
    </SelectComponents.Option>
  );
};

export const IconControl = (
  props: SelectComponents.ControlProps<ResolutionTypeOption, false>,
) => {
  const value = props.getValue();
  const inputValue = props.selectProps.inputValue;
  const selectedValue: ResolutionTypeOption | undefined = Array.isArray(value)
    ? value[0]
    : value;
  const Icon = selectedValue ? getResolutionIcon(selectedValue.value) : null;
  const isDestructive = selectedValue
    ? getIsDestructive(selectedValue.value)
    : false;

  return selectedValue ? (
    <SelectComponents.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        alignItems: 'center',
        color: 'feature.primary',
      }}
      className="resolution-type-control"
    >
      {Icon &&
        createElement(Icon, {
          ...ICON_STYLES,
          color: isDestructive
            ? 'interaction.destructive'
            : 'feature.secondary',
          ml: 1,
        })}
      {props.children}
    </SelectComponents.Control>
  ) : (
    <SelectComponents.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        // When we have a value in the input field we want to make sure that input text
        // is the placeholder color, otherwise default to feature.primary
        color: inputValue ? 'feature.primary' : 'feature.placeholder',
      }}
      className="resolution-type-control"
    />
  );
};

export const ResolutionTypeSelect = (
  props: Omit<
    SelectProps<ResolutionTypeOption>,
    'options' | 'value' | 'onChange'
  > & {
    value?: ResolutionTypeOption | '';
    onChange: (value: ResolutionTypeOption) => void;
  },
) => {
  return <Select {...props} className="resolution-type-select" />;
};
